import * as React from 'react';
import { nameToFirstInitials } from 'utils/shared/name/nameToFirstInitials';
import CloseIcon from 'svg/CloseIcon';
import { Button, ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';
import { RemainingRound, VairRound } from './types';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  onSubmit: () => void;
  rounds: VairRound[];
  remainingRounds: RemainingRound[];
  totalMatches: number;
  totalRounds: number;
  totalIncompleteMatches: number;
  eventName: string;
  isLoading?: boolean;
}

export default function ModalScoreOverview({
  isOpen,
  closeModal,
  onSubmit,
  rounds,
  remainingRounds,
  totalMatches,
  totalRounds,
  totalIncompleteMatches,
  eventName,
  isLoading = false,
}: Props) {
  const completedMatches = totalMatches - totalIncompleteMatches;

  return (
    <Modal
      isOpen={isOpen}
      handleClose={closeModal}
      className="block focus:outline-none focus:ring-4"
      classNameMaxWidth="md:max-w-lg xs:max-w-md"
    >
      <div className="flex max-h-[90dvh] flex-col">
        {/* Modal header */}
        <div className="flex items-center justify-between border-b border-color-border-input-lightmode px-4 py-4 dark:border-color-border-input-darkmode">
          <h3 className="typography-product-heading-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Score overview
          </h3>
          <ButtonText onClick={closeModal}>
            <CloseIcon className="h-5 w-5" />
          </ButtonText>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* Event info + stats */}
          <div className="flex flex-col items-center gap-2 p-4">
            <img src="/images/tourney-dashboard/eyes.png" alt="" className="h-16 w-16" />
            <h2 className="typography-product-heading-mobile text-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {eventName}
            </h2>
            <p className="typography-product-body text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Review all rounds and scores before submitting. You can submit even with incomplete
              matches.
            </p>

            {/* Stats */}
            <div className="mt-2 flex w-full gap-3">
              <div className="flex flex-1 flex-col items-center gap-1 rounded-md border border-color-border-input-lightmode px-2 py-2 dark:border-color-border-input-darkmode">
                <p className="typography-product-heading-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  {totalRounds}
                </p>
                <p className="typography-product-chips-filters text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  Total rounds
                </p>
              </div>
              <div className="flex flex-1 flex-col items-center gap-1 rounded-md border border-color-border-input-lightmode px-2 py-2 dark:border-color-border-input-darkmode">
                <p className="typography-product-heading-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  {totalMatches}
                </p>
                <p className="typography-product-chips-filters text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  Total matches
                </p>
              </div>
              <div className="flex flex-1 flex-col items-center gap-1 rounded-md border border-color-border-input-lightmode px-2 py-2 dark:border-color-border-input-darkmode">
                <p className="typography-product-heading-mobile text-color-text-brand">
                  {completedMatches}
                </p>
                <p className="typography-product-chips-filters text-color-text-brand">
                  Completed
                </p>
              </div>
            </div>
          </div>

          {/* Rounds list */}
          <div className="flex-1 space-y-3 overflow-y-auto px-4 pb-4">
            {rounds.map((round) => {
              const roundMatches = round.matches || [];
              return (
                <div
                  key={round.id}
                  className="rounded-[20px] border border-color-border-input-lightmode bg-color-bg-lightmode-primary p-4 shadow-md dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary"
                >
                  <div className="mb-4 flex items-center justify-between">
                    <h4 className="typography-product-heading-compact-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                      {round.title || `Round ${round.roundOrder + 1}`}
                    </h4>
                    <span className="typography-product-caption text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                      Matches: {roundMatches.length}
                    </span>
                  </div>

                  <div className="mb-4 h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />

                  <div className="space-y-2">
                    {roundMatches.map((match) => {
                      const team1Members = match.team1?.members || [];
                      const team2Members = match.team2?.members || [];
                      const games = match.games || [];
                      const finalGame = games[games.length - 1];
                      const team1Score = finalGame?.team1Score;
                      const team2Score = finalGame?.team2Score;
                      const hasScores =
                        typeof team1Score === 'number' && typeof team2Score === 'number';
                      const team1Won = hasScores && team1Score > team2Score;
                      const team2Won = hasScores && team2Score > team1Score;

                      return (
                        <div
                          key={match.id}
                          className="flex h-14 overflow-clip rounded-lg border border-color-border-input-lightmode dark:border-color-border-input-darkmode"
                        >
                          {/* Team 1 names */}
                          <div className="flex flex-1 flex-col justify-center bg-color-bg-lightmode-secondary p-2 dark:bg-color-bg-darkmode-secondary">
                            {team1Members.map((member) => (
                              <p
                                key={member.id}
                                className="typography-product-caption overflow-hidden text-ellipsis whitespace-nowrap text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
                              >
                                {nameToFirstInitials(
                                  member.userProfile?.preferredName ||
                                    member.userProfile?.fullName ||
                                    '',
                                )}
                              </p>
                            ))}
                          </div>

                          {/* Score */}
                          <div className="flex w-14 shrink-0 items-center justify-center bg-color-bg-lightmode-secondary p-2 dark:bg-color-bg-darkmode-secondary">
                            {hasScores ? (
                              <p className="typography-product-button-label-medium text-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                                <span
                                  className={classNames(
                                    team1Won ? 'text-color-text-brand' : '',
                                  )}
                                >
                                  {team1Score}
                                </span>
                                {` - `}
                                <span
                                  className={classNames(
                                    team2Won ? 'text-color-text-brand' : '',
                                  )}
                                >
                                  {team2Score}
                                </span>
                              </p>
                            ) : (
                              <p className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                                VS
                              </p>
                            )}
                          </div>

                          {/* Team 2 names */}
                          <div className="flex flex-1 flex-col justify-center bg-color-bg-lightmode-secondary p-2 dark:bg-color-bg-darkmode-secondary">
                            {team2Members.map((member) => (
                              <p
                                key={member.id}
                                className="typography-product-caption overflow-hidden text-ellipsis whitespace-nowrap text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
                              >
                                {nameToFirstInitials(
                                  member.userProfile?.preferredName ||
                                    member.userProfile?.fullName ||
                                    '',
                                )}
                              </p>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Incomplete rounds */}
            {remainingRounds.length > 0 &&
              remainingRounds.map((round) => (
                <div
                  key={round.roundOrder}
                  className="rounded-[20px] border border-color-border-input-lightmode bg-color-bg-lightmode-primary p-4 opacity-50 shadow-md dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="typography-product-heading-compact-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                      {round.title || `Round ${round.roundOrder + 1}`}
                    </h4>
                    <span className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                      {round.incompleteMatches} incomplete
                    </span>
                  </div>
                </div>
              ))}
          </div>

          {/* Footer */}
          <div className="flex shrink-0 flex-col gap-4 border-t border-color-border-input-lightmode p-4 dark:border-color-border-input-darkmode">
            <Button variant="inverted" size="lg" onClick={closeModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="brand" size="lg" onClick={onSubmit} loading={isLoading}>
              Confirm & Submit
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
